import { cn } from '@/lib/cn'
import { useT } from '@/i18n/provider'

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'accent'

interface GlassBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  dot?: boolean
}

const variantStyles: Record<BadgeVariant, string> = {
  default:  'bg-white/10 text-white/70 border-white/15',
  success:  'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  warning:  'bg-amber-500/20 text-amber-300 border-amber-500/30',
  danger:   'bg-red-500/20 text-red-300 border-red-500/30',
  accent:   'bg-[var(--oc-accent)]/20 text-[var(--oc-accent-light)] border-[var(--oc-accent)]/30',
}

const dotStyles: Record<BadgeVariant, string> = {
  default:  'bg-white/50',
  success:  'bg-emerald-400',
  warning:  'bg-amber-400',
  danger:   'bg-red-400',
  accent:   'bg-[var(--oc-accent-light)]',
}

export function GlassBadge({ variant = 'default', dot, className, children, ...props }: GlassBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5',
        'rounded-full border text-xs font-medium',
        'backdrop-filter backdrop-blur-sm',
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full', dotStyles[variant])} />}
      {children}
    </span>
  )
}

// Agent status badge
export function AgentStatusBadge({ status }: { status: string }) {
  const t = useT()
  const map: Record<string, BadgeVariant> = {
    ACTIVE:   'success',
    DRAFT:    'default',
    PAUSED:   'warning',
    ARCHIVED: 'danger',
  }
  const keyMap = {
    ACTIVE: 'status.ACTIVE',
    DRAFT: 'status.DRAFT',
    PAUSED: 'status.PAUSED',
    ARCHIVED: 'status.ARCHIVED',
  } as const
  const label = t(keyMap[status as keyof typeof keyMap] ?? 'status.DRAFT')

  return <GlassBadge variant={map[status] ?? 'default'} dot>{label}</GlassBadge>
}
